import React, { ReactNode, useCallback, useRef, useState } from 'react';
import O_Header from './O_Header';
import O_HeaderMobile from './O_HeaderMobile';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usersApi, mapSearchResultToUser } from '@/services/usersApi';
import { capsulesApi, Capsule } from '@/services/capsulesApi';
import { User } from '@/types';
import O_FeedSearchResults from '@/components/O_Organisms/O_FeedSearchResults/O_FeedSearchResults';

interface O_HeaderResponsiveProps {
  showCreateButton?: boolean;
  showSearchInput?: boolean;
  actionSlot?: ReactNode;
  hideRightNav?: boolean;
}

export default function O_HeaderResponsive({
  showCreateButton = true,
  showSearchInput = true,
  actionSlot,
  hideRightNav = false,
}: O_HeaderResponsiveProps) {
  const isMobile = useIsMobile(968);

  const [searchQuery, setSearchQuery] = useState('');
  const [userResults, setUserResults] = useState<User[]>([]);
  const [capsuleResults, setCapsuleResults] = useState<Capsule[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (trimmed: string) => {
    setIsSearching(true);
    setShowResults(true);
    try {
      const [usersRes, capsulesRes] = await Promise.all([
        usersApi.searchUsers({ q: trimmed, limit: 5 }),
        capsulesApi.searchCapsules({ q: trimmed, limit: 10 }),
      ]);
      if (usersRes.success && usersRes.data?.users) {
        setUserResults(
          usersRes.data.users.map(result => mapSearchResultToUser(result))
        );
      } else {
        setUserResults([]);
      }
      if (capsulesRes.success && capsulesRes.data?.capsules) {
        setCapsuleResults(capsulesRes.data.capsules);
      } else {
        setCapsuleResults([]);
      }
    } catch {
      setUserResults([]);
      setCapsuleResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!query.trim()) {
        setUserResults([]);
        setCapsuleResults([]);
        setShowResults(false);
        return;
      }
      debounceRef.current = setTimeout(() => {
        runSearch(query.trim());
      }, 400);
    },
    [runSearch]
  );

  const handleSearchSubmit = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      setSearchQuery(trimmed);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!trimmed) {
        setUserResults([]);
        setCapsuleResults([]);
        setShowResults(false);
        return;
      }
      runSearch(trimmed);
    },
    [runSearch]
  );

  const handleCloseResults = useCallback(() => {
    setShowResults(false);
  }, []);

  if (isMobile) {
    return (
      <>
        <O_HeaderMobile
          showSearchInput={showSearchInput}
          actionSlot={actionSlot}
          hideRightNav={hideRightNav}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
        />
        {showSearchInput && showResults && (
          <O_FeedSearchResults
            query={searchQuery}
            users={userResults}
            capsules={capsuleResults}
            isLoading={isSearching}
            onClose={handleCloseResults}
          />
        )}
      </>
    );
  }

  return (
    <>
      <O_Header
        showCreateButton={showCreateButton}
        showSearchInput={showSearchInput}
        actionSlot={actionSlot}
        hideRightNav={hideRightNav}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
      />
      {showSearchInput && showResults && (
        <O_FeedSearchResults
          query={searchQuery}
          users={userResults}
          capsules={capsuleResults}
          isLoading={isSearching}
          onClose={handleCloseResults}
        />
      )}
    </>
  );
}
